import React from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { MetricCardProps } from '../utils/dashboardTypes';
import { getIcon } from '../utils/iconMapping';
import { ArrowUpIcon, ArrowDownIcon } from '../utils/iconMapping';

export function MetricCard({
  label,
  value,
  icon,
  trend,
  onClick,
  subtitle,
  compact
}: MetricCardProps) {
  const IconComponent = getIcon(icon);

  return (
    <Card
      onClick={onClick}
      className={cn(
        "group transition-all duration-300",
        // Clickable styles
        onClick && "cursor-pointer hover:shadow-xl hover:-translate-y-1",
        // Compact vs Full styles
        compact
          ? "border-0 border-b border-slate-200 dark:border-slate-800 rounded-none flex-1 min-h-0" +
            " hover:bg-white dark:hover:bg-slate-900 hover:shadow-[inset_4px_0_0_hsl(var(--primary))]" +
            " hover:translate-x-1 active:translate-x-0.5"
          : "border-slate-200 dark:border-slate-800 rounded-lg" +
            " bg-gradient-to-br from-white to-slate-50" +
            " dark:from-slate-900 dark:to-slate-800" +
            " hover:border-teal-600 dark:hover:border-teal-400",
        // Remove transforms on reduced motion preference
        "motion-reduce:transform-none"
      )}
    >
      <CardHeader className={cn(
        "flex flex-row items-center justify-between space-y-0",
        compact ? "pb-2 pt-3 px-4" : "pb-2"
      )}>
        <span className={cn(
          "font-mono uppercase tracking-wider font-medium",
          "text-slate-500 dark:text-slate-400",
          compact ? "text-xs" : "text-sm"
        )}>
          {label}
        </span>
        <div className={cn(
          "text-teal-600 dark:text-teal-400",
          "transition-transform duration-200 group-hover:scale-110",
          "motion-reduce:transform-none"
        )}>
          <IconComponent className={compact ? "w-5 h-5" : "w-6 h-6"} />
        </div>
      </CardHeader>

      <CardContent className={cn(
        compact ? "pb-3 px-4" : "pb-6"
      )}>
        {/* Large serif number - Editorial Precision */}
        <div className={cn(
          "font-serif font-bold leading-none mb-2",
          "text-slate-900 dark:text-slate-100",
          compact
            ? "text-4xl md:text-5xl"
            : "text-5xl md:text-6xl lg:text-7xl xl:text-8xl"
        )}>
          {value.toLocaleString()}
        </div>

        {/* Subtitle */}
        {subtitle && (
          <p className={cn(
            "text-sm text-slate-600 dark:text-slate-400",
            "mt-2"
          )}>
            {subtitle}
          </p>
        )}

        {/* Trend Indicator */}
        {trend && (
          <div className={cn(
            "flex items-center gap-1 mt-3 text-xs font-medium",
            trend.direction === 'up' && "text-emerald-600 dark:text-emerald-400",
            trend.direction === 'down' && "text-rose-600 dark:text-rose-400",
            trend.direction === 'neutral' && "text-slate-600 dark:text-slate-400"
          )}>
            {trend.direction === 'up' && <ArrowUpIcon className="w-3.5 h-3.5" />}
            {trend.direction === 'down' && <ArrowDownIcon className="w-3.5 h-3.5" />}
            <span>{trend.label}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
